import { onBeforeUnmount, onMounted, ref, watch, type Ref } from "vue";
import { isJust, isNothing, type Maybe } from "../../lib/maybe";

export type ZwSelectionRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

/**
 * Paint selection rectangles ourselves. macOS WKWebView ignores ::selection on
 * form controls and often on contenteditable too, so inputs draw their own.
 */
export function usePaintedSelection(elRef: Ref<Maybe<HTMLElement>>): {
  selectionRects: Ref<ZwSelectionRect[]>;
  refreshSelectionPaint: () => void;
} {
  const rects = ref<ZwSelectionRect[]>([]);
  let raf = 0;

  function clear(): void {
    rects.value = [];
  }

  function measure(): void {
    const elM = elRef.value;
    if (isNothing(elM)) {
      clear();
      return;
    }
    const el = elM.value;
    const sel = window.getSelection();
    if (sel === null || sel.rangeCount === 0 || sel.isCollapsed === true) {
      clear();
      return;
    }
    const range = sel.getRangeAt(0);
    if (el.contains(range.commonAncestorContainer) === false) {
      clear();
      return;
    }

    const origin =
      el.closest(".zw-input-shell")?.querySelector(".zw-input-face") ??
      el.closest(".zw-input-shell") ??
      el;
    const box = origin.getBoundingClientRect();
    const next: ZwSelectionRect[] = [];
    for (const r of range.getClientRects()) {
      if (r.width < 0.5 || r.height < 0.5) continue;
      // Viewport rects − face box (sel layer is position:absolute; inset:0 on face).
      next.push({
        top: r.top - box.top,
        left: r.left - box.left,
        width: r.width,
        height: r.height,
      });
    }
    rects.value = next;
  }

  function schedule(): void {
    if (raf !== 0) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      raf = 0;
      measure();
    });
  }

  function onScroll(): void {
    schedule();
  }

  onMounted(() => {
    document.addEventListener("selectionchange", schedule);
  });

  watch(
    elRef,
    (elM, prevM) => {
      if (isJust(prevM)) prevM.value.removeEventListener("scroll", onScroll);
      if (isJust(elM))
        elM.value.addEventListener("scroll", onScroll, { passive: true });
      schedule();
    },
    { flush: "post" },
  );

  onBeforeUnmount(() => {
    document.removeEventListener("selectionchange", schedule);
    if (isJust(elRef.value))
      elRef.value.value.removeEventListener("scroll", onScroll);
    if (raf !== 0) cancelAnimationFrame(raf);
  });

  return { selectionRects: rects, refreshSelectionPaint: schedule };
}
